import React, { useState, useMemo, useEffect, useRef } from "react";
import { FaBuilding, FaChevronDown, FaEdit, FaPhone, FaUser, FaWhatsapp, FaEnvelope, FaSlidersH, FaTrashAlt } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import CustomersTable from "./CustomersTable";
import { SupportSortableHeader } from "./SupportSortableHeader";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, RowsPerPageSelect } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import useCompactViewport from "../hooks/useCompactViewport";
import { formatPascal, formatFullName, formatPhone, formatEmail } from "../utils/formatters";
import {
  digitsOnly,
  joinPhoneDisplay,
  joinPhoneE164,
  normalizeCountryCode,
} from "../utils/phone";
import "../assets/css/DigixLeads.css";

const CUSTOMERS_ROWS_PER_PAGE_KEY = "customersRowsPerPage";

/** Map table "Created On" display (e.g. en-GB dd/mm/yyyy) to YYYY-MM-DD for comparisons. */
function customerCreatedDateToISO(createdAtDisplay) {
  if (!createdAtDisplay || createdAtDisplay === "—") return "";
  const datePart = String(createdAtDisplay).split(" ")[0] || "";
  if (datePart.includes("-") && datePart.length >= 10) return datePart.substring(0, 10);
  const [d, m, y] = datePart.split("/");
  if (y && m && d) {
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  return "";
}

/**
 * Shared API helper for searching customers.
 * Used by both this page component and the DigixLeads container.
 */
export async function searchCustomersApi(filters, servicesData, allCities) {
  const params = new URLSearchParams();

  const nameOrPhoneValue = (filters.nameOrPhone || "").trim();
  if (nameOrPhoneValue) {
    const digitsOnly = nameOrPhoneValue.replace(/\D/g, "");
    const hasDigits = digitsOnly.length > 0;
    if (hasDigits) {
      params.append("phone", nameOrPhoneValue);
    } else {
      params.append("name", nameOrPhoneValue);
    }
  }

  if (filters.status && String(filters.status).trim()) {
    params.append("status", String(filters.status).trim());
  }

  if (filters.service && filters.service !== "All Services") {
    for (const category of servicesData || []) {
      if (category.subcategories && Array.isArray(category.subcategories)) {
        const subcategory = category.subcategories.find(
          (sub) => sub.subcategory_name === filters.service
        );
        if (subcategory && subcategory.subcategory_id) {
          params.append("service_id", subcategory.subcategory_id);
          break;
        }
      }
    }
  }

  if (filters.city) {
    const cityObj = (allCities || []).find(
      (c) =>
        c.city_name?.toLowerCase() === filters.city.toLowerCase() ||
        c.City_Name?.toLowerCase() === filters.city.toLowerCase()
    );
    if (cityObj) {
      const cityId = cityObj.city_id || cityObj.City_Id;
      if (cityId) {
        params.append("city_id", cityId);
      }
    }
  }

  if (filters.domain) {
    params.append("domain", filters.domain.trim());
  }

  if (filters.customerSource) {
    params.append("source", filters.customerSource.trim());
  }

  if (filters.nextFollowupDate) {
    const dateValue = filters.nextFollowupDate.trim();
    if (dateValue) {
      params.append("next_followup_date", dateValue);
    }
  }

  if (filters.createdFrom) {
    const v = filters.createdFrom.trim();
    if (v) params.append("created_from", v);
  }
  if (filters.createdTo) {
    const v = filters.createdTo.trim();
    if (v) params.append("created_to", v);
  }

  const response = await axiosInstance.get(
    `/search-clients?${params.toString()}`
  );

  if (!response.data || !response.data.results) {
    return [];
  }

  return response.data.results.map((item) => {
    const customer = item.client;
    const address = item.address || {};
    const followups = item.followups || [];
    // API returns followups ordered by Created_On desc — first is latest
    const latestFollowup = followups.length > 0 ? followups[0] : null;
    let nextFollowupDateStr = "—";
    let lastFollowupDateStr = "—";
    let lastFollowupNotesStr = "—";

    if (latestFollowup) {
      if (latestFollowup.Next_Followup_Date) {
        const date = new Date(latestFollowup.Next_Followup_Date);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          nextFollowupDateStr = `${day}/${month}/${year}`;
        }
      }
      if (latestFollowup.Followup_Date) {
        const date = new Date(latestFollowup.Followup_Date);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          lastFollowupDateStr = `${day}/${month}/${year}`;
        }
      }
      if (latestFollowup.Remarks) {
        lastFollowupNotesStr = latestFollowup.Remarks;
      }
    }

    return {
      id: customer.client_id,
      name: `${customer.First_Name || ""} ${
        customer.Last_Name || ""
      }`.trim(),
      customerType: customer.Customer_Type || customer.customer_type || "—",
      email: customer.Email || "—",
      phone: customer.Phone || "—",
      countryCode: customer.Country_Code || customer.country_code || "",
      companyName: customer.Company_Name || "—",
      ownerBusinessCount: Number(customer.Owner_Business_Count) || 0,
      domainId: customer.Domain_Id ?? null,
      domain: customer.Domain || "—",
      companyEmail: customer.Company_Email || "—",
      companyPhone: customer.Company_Phone || "—",
      location: (() => {
        const city = String(address.City_Name || address.city_name || "").trim();
        const country = String(address.Country_Name || address.country_name || "").trim();
        if (city && country) return `${city}, ${country}`;
        if (city) return city;
        if (country) return country;
        return "—";
      })(),
      locationShort: (() => {
        const city = String(address.City_Name || address.city_name || "").trim();
        const country = String(
          address.Country_Short_Code || address.Country_Name || address.country_name || ""
        ).trim();
        return [city, country].filter(Boolean).join(", ") || "—";
      })(),
      services: customer.Services || [],
      status: customer.Status || "—",
      source: customer.Source || "—",
      createdAt: customer.Created_On
        ? new Date(customer.Created_On).toLocaleDateString("en-GB")
        : "—",
      createdBy:
        customer.Created_By_Name ||
        (customer.Created_By != null ? `User #${customer.Created_By}` : "—"),
      updatedBy:
        customer.Updated_By_Name ||
        (customer.Updated_By != null ? `User #${customer.Updated_By}` : "—"),
      updatedAt: customer.Updated_On
        ? new Date(customer.Updated_On).toLocaleDateString("en-GB")
        : "—",
      updatedAtMs: customer.Updated_On
        ? (() => {
            const t = new Date(customer.Updated_On).getTime();
            return Number.isFinite(t) ? t : 0;
          })()
        : 0,
      lastFollowupDate: lastFollowupDateStr,
      notes: lastFollowupNotesStr,
      nextFollowupDate: nextFollowupDateStr,
      isQuoteSent: customer.Is_Quote_Sent ? "Yes" : "No",
      customerNotes: customer.Notes || customer.notes || "—",
    };
  });
}

/**
 * CustomersLeadsPage
 *
 * Encapsulates filter state, search logic, free search, and pagination
 * for the Customers tab.
 */
function CustomersLeadsPage({
  customers = [],
  services = [],
  servicesData = [],
  allCities = [],
  loadingCities = false,
  domains = [],
  onViewLead,
  onEditLead,
  onDeleteTenant,
  onAddTenantClick,
  onLoginFixesClick,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const CUSTOMERS_CURRENT_PAGE_KEY = "customersCurrentPage";
  const isCompactViewport = useCompactViewport();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filters, setFilters] = useState({
    nameOrPhone: "",
    city: "",
    domain: "",
    service: "All Services",
    status: "Active",
    customerSource: "",
    nextFollowupDate: "",
    createdFrom: "",
    createdTo: "",
    freeSearch: "",
  });

  const [searchResults, setSearchResults] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    nameOrPhone: "",
    city: "",
    domain: "",
    service: "All Services",
    status: "Active",
    customerSource: "",
    nextFollowupDate: "",
    createdFrom: "",
    createdTo: "",
    freeSearch: "",
  });
  const searchRequestSeqRef = useRef(0);
  const skipPageResetOnFiltersSyncRef = useRef(true);
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOMERS_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOMERS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 10;
  });
  const [sortColumn, setSortColumn] = useState("updatedAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [columnWidths, setColumnWidths] = useState({});
  const resizeRef = React.useRef({ startX: 0, startWidth: 0, columnIndex: null });
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");
  const serviceDropdownRef = useRef(null);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const locationDropdownRef = useRef(null);

  useEffect(() => {
    const preset = location.state?.dashboardCustomerStatusPreset;
    if (typeof preset !== "string" || !preset) return;
    const normalizedPreset = preset.toLowerCase();
    if (normalizedPreset !== "active" && normalizedPreset !== "activeprojects") return;
    setFilters((prev) => ({ ...prev, status: "Active" }));
    setCurrentPage(1);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) {
        setDomainDropdownOpen(false);
        setDomainSearchQuery("");
      }
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target)) {
        setServiceDropdownOpen(false);
        setServiceSearchQuery("");
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target)) {
        setLocationDropdownOpen(false);
        setLocationSearchQuery("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const emptyFilters = {
    nameOrPhone: "",
    city: "",
    domain: "",
    service: "All Services",
    status: "Active",
    customerSource: "",
    nextFollowupDate: "",
    createdFrom: "",
    createdTo: "",
    freeSearch: "",
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchResults(null);
    setSortColumn("updatedAt");
    setSortDirection("desc");
    setCurrentPage(1);
    setDomainDropdownOpen(false);
    setServiceDropdownOpen(false);
    setLocationDropdownOpen(false);
    setDomainSearchQuery("");
    setServiceSearchQuery("");
    setLocationSearchQuery("");
  };

  // Persist pagination page so View/Edit -> Back returns to the same page.
  useEffect(() => {
    try {
      localStorage.setItem(CUSTOMERS_CURRENT_PAGE_KEY, String(currentPage));
    } catch {
      /* ignore storage errors */
    }
  }, [currentPage]);

  const hasSearchFilters = (f) => {
    return (
      f.nameOrPhone ||
      f.city ||
      (f.status && String(f.status).trim()) ||
      (f.service && f.service !== "All Services") ||
      f.domain ||
      f.customerSource ||
      f.nextFollowupDate ||
      f.createdFrom ||
      f.createdTo
    );
  };

  useEffect(() => {
    setAppliedFilters(filters);
    if (skipPageResetOnFiltersSyncRef.current) {
      skipPageResetOnFiltersSyncRef.current = false;
    } else {
      setCurrentPage(1);
    }
  }, [filters]);

  useEffect(() => {
    const seq = ++searchRequestSeqRef.current;
    const t = setTimeout(async () => {
      if (!hasSearchFilters(filters)) {
        if (seq === searchRequestSeqRef.current) {
          setSearchResults(null);
        }
        return;
      }
      try {
        const results = await searchCustomersApi(filters, servicesData, allCities);
        if (seq === searchRequestSeqRef.current) {
          setSearchResults(results);
        }
      } catch (error) {
        const msg =
          error.response?.data?.error ||
          error.message ||
          "Error searching customers.";
        console.error("Error searching customers:", msg);
        if (seq === searchRequestSeqRef.current) {
          setSearchResults([]);
          alert(
            "Error searching customers: " +
              (error.response?.data?.error || error.message)
          );
        }
      }
    }, 350);
    return () => clearTimeout(t);
  }, [filters, servicesData, allCities]);

  const baseCustomers = useMemo(
    () => (searchResults !== null ? searchResults : customers),
    [searchResults, customers]
  );

  const filteredCustomers = useMemo(() => {
    const af = appliedFilters;
    return baseCustomers.filter((lead) => {
      if (af.nameOrPhone && searchResults === null) {
        const searchValue = af.nameOrPhone.toLowerCase().trim();
        const nameMatch = lead.name?.toLowerCase().includes(searchValue);
        const phoneMatch = lead.phone?.includes(af.nameOrPhone.trim());
        if (!nameMatch && !phoneMatch) return false;
      }
      if (
        af.city &&
        !lead.location?.toLowerCase().includes(af.city.toLowerCase())
      ) {
        return false;
      }
      if (
        af.domain &&
        !lead.domain?.toLowerCase().includes(af.domain.toLowerCase())
      ) {
        return false;
      }
      if (
        af.service &&
        af.service !== "All Services" &&
        !lead.services?.includes(af.service)
      ) {
        return false;
      }
      if (af.status && String(af.status).trim()) {
        const leadStatus = String(lead.status || "").trim().toLowerCase();
        if (leadStatus !== String(af.status).trim().toLowerCase()) return false;
      }
      if (af.nextFollowupDate) {
        const filterDate = af.nextFollowupDate; // YYYY-MM-DD
        if (!lead.nextFollowupDate || lead.nextFollowupDate === "—") {
          return false;
        }
        const datePart = lead.nextFollowupDate.split(" ")[0] || "";
        let leadDateISO = "";
        if (datePart.includes("-") && datePart.length >= 10) {
          leadDateISO = datePart.substring(0, 10);
        } else {
          const [d, m, y] = datePart.split("/");
          if (y && m && d)
            leadDateISO = `${y}-${m.padStart(2, "0")}-${d.padStart(
              2,
              "0"
            )}`;
        }
        if (leadDateISO !== filterDate) return false;
      }
      if (af.createdFrom) {
        const leadISO = customerCreatedDateToISO(lead.createdAt);
        if (!leadISO || leadISO < af.createdFrom) return false;
      }
      if (af.createdTo) {
        const leadISO = customerCreatedDateToISO(lead.createdAt);
        if (!leadISO || leadISO > af.createdTo) return false;
      }
      return true;
    });
  }, [baseCustomers, appliedFilters, searchResults]);

  const freeSearchFiltered = useMemo(() => {
    if (!appliedFilters.freeSearch) return filteredCustomers;
    const searchValue = appliedFilters.freeSearch.toLowerCase().trim();
    if (!searchValue) return filteredCustomers;

    return filteredCustomers.filter((lead) => {
      const name = (lead.name || "").toLowerCase();
      const email = (lead.email || "").toLowerCase();
      const phone = (lead.phone || "").toLowerCase();
      const location = (lead.location || "").toLowerCase();
      const createdAt = (lead.createdAt || "").toLowerCase();
      const lastFollowupDate = (lead.lastFollowupDate || "").toLowerCase();
      const notes = (lead.notes || "").toLowerCase();
      const nextFollowupDate = (lead.nextFollowupDate || "").toLowerCase();
      const status = (lead.status || "").toLowerCase();
      const source = (lead.source || "").toLowerCase();
      const servicesStr = Array.isArray(lead.services)
        ? lead.services.join(", ").toLowerCase()
        : (lead.services || "").toLowerCase();
      const customerType = (lead.customerType || "").toLowerCase();

      return (
        name.includes(searchValue) ||
        customerType.includes(searchValue) ||
        email.includes(searchValue) ||
        phone.includes(searchValue) ||
        location.includes(searchValue) ||
        createdAt.includes(searchValue) ||
        lastFollowupDate.includes(searchValue) ||
        notes.includes(searchValue) ||
        nextFollowupDate.includes(searchValue) ||
        status.includes(searchValue) ||
        source.includes(searchValue) ||
        servicesStr.includes(searchValue)
      );
    });
  }, [filteredCustomers, appliedFilters.freeSearch]);

  const totalPages = Math.max(1, Math.ceil(freeSearchFiltered.length / rowsPerPage));

  const getSortValue = (lead, columnId) => {
    switch (columnId) {
      case "name":
        return String(lead.name || "").toLowerCase();
      case "customerType":
        return String(lead.customerType || "").toLowerCase();
      case "phone":
        return String(lead.phone || "").toLowerCase();
      case "location":
        return String(lead.location || "").toLowerCase();
      case "services": {
        const servicesStr = Array.isArray(lead.services) ? lead.services.join(", ") : (lead.services || "");
        return String(servicesStr).toLowerCase();
      }
      case "domain":
        return String(lead.domain || "").toLowerCase();
      case "status":
        return String(lead.status || "").toLowerCase();
      case "isQuoteSent":
        return String(lead.isQuoteSent || "").toLowerCase();
      case "notes":
        return String(lead.customerNotes || lead.notes || "").toLowerCase();
      case "lastFollowupDate":
      case "nextFollowupDate":
      case "createdAt":
      case "updatedAt": {
        if (columnId === "updatedAt" && Number.isFinite(Number(lead.updatedAtMs))) {
          return Number(lead.updatedAtMs);
        }
        const raw =
          columnId === "lastFollowupDate"
            ? lead.lastFollowupDate
            : columnId === "nextFollowupDate"
              ? lead.nextFollowupDate
              : columnId === "updatedAt"
                ? lead.updatedAt
                : lead.createdAt;
        const iso = customerCreatedDateToISO(raw);
        const ts = iso ? new Date(iso).getTime() : 0;
        return Number.isFinite(ts) ? ts : 0;
      }
      case "createdBy":
        return String(lead.createdBy || "").toLowerCase();
      case "updatedBy":
        return String(lead.updatedBy || "").toLowerCase();
      case "followupNotes":
        return String(lead.notes || "").toLowerCase();
      case "source":
        return String(lead.source || "").toLowerCase();
      case "companyName":
        return String(lead.companyName || "").toLowerCase();
      case "email":
        return String(lead.companyEmail || lead.email || "").toLowerCase();
      default:
        return "";
    }
  };

  const sortedCustomers = useMemo(() => {
    const column = sortColumn || "updatedAt";
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFiltered].sort((a, b) => {
      const va = getSortValue(a, column);
      const vb = getSortValue(b, column);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return Number(b.updatedAtMs || 0) - Number(a.updatedAtMs || 0) || Number(a.id || 0) - Number(b.id || 0);
    });
  }, [freeSearchFiltered, sortColumn, sortDirection]);

  // Clamp page when the data set changes (e.g., after returning from View/Edit).
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentLeads = sortedCustomers.slice(startIndex, endIndex);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(CUSTOMERS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const getColumnWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) {
      return `${columnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const getColumnMinWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) {
      return `${columnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.parentElement;
    if (!th) return;

    const startWidth = th.offsetWidth;
    const startX = e.clientX;

    resizeRef.current = {
      startX,
      startWidth,
      columnIndex
    };

    document.body.classList.add("resizing");

    const handleMouseMove = (moveEvent) => {
      if (resizeRef.current.columnIndex === null) return;
      const diff = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = Math.max(80, resizeRef.current.startWidth + diff);

      setColumnWidths((prev) => ({
        ...prev,
        [resizeRef.current.columnIndex]: newWidth
      }));
    };

    const handleMouseUp = () => {
      resizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove("resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Laptop/desktop: inline right after Clear. Compact: own row outside the collapsible filters.
  const pageActions =
    onAddTenantClick || onLoginFixesClick ? (
      <div className={`header-actions${isCompactViewport ? "" : " customers-inline-actions"}`}>
        {onLoginFixesClick ? (
          <button type="button" className="btn-clear btn-login-fixes" onClick={onLoginFixesClick}>
            Login fixes
          </button>
        ) : null}
        {onAddTenantClick ? (
          <button type="button" className="btn-primary btn-add-lead" onClick={onAddTenantClick}>
            New Client
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <div className="customers-page">
      {/* Filters (left), New Customer (right); API search runs while typing (debounced). */}
      <div className={`search-actions-row co-filters-collapsible${filtersOpen || !isCompactViewport ? " is-open" : ""}`}>
        {isCompactViewport ? (
          <div className="co-filter-search-bar">
            <input
              type="text"
              className="co-filter-search-bar__input"
              placeholder="Search name or phone"
              value={filters.nameOrPhone}
              onChange={(e) => handleFilterChange("nameOrPhone", e.target.value)}
              aria-label="Search name or phone"
            />
            <button
              type="button"
              className={`co-filter-search-bar__icon${filtersOpen ? " is-active" : ""}`}
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              aria-label={filtersOpen ? "Hide Filters" : "Show Filters"}
              title={filtersOpen ? "Hide Filters" : "Show Filters"}
            >
              <FaSlidersH aria-hidden />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="co-filters-toggle"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            aria-label={filtersOpen ? "Hide Filters" : "Show Filters"}
            title={filtersOpen ? "Hide Filters" : "Show Filters"}
          >
            <FaSlidersH aria-hidden />
            <span className="co-filters-toggle__label">{filtersOpen ? "Hide Filters" : "Show Filters"}</span>
          </button>
        )}
        <div className="co-filters-body">
        <div className="search-filters-row search-filters-row--customers-dates">
          {!isCompactViewport ? (
          <input
            type="text"
            className="search-filter-input customers-filter-name-phone"
            placeholder="Search name or phone"
            value={filters.nameOrPhone}
            onChange={(e) => handleFilterChange("nameOrPhone", e.target.value)}
          />
          ) : null}
          <div className="domain-filter-dropdown" ref={locationDropdownRef}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              onClick={() => !loadingCities && setLocationDropdownOpen((o) => !o)}
              role="button"
              tabIndex={loadingCities ? -1 : 0}
              onKeyDown={(e) => {
                if (!loadingCities && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  setLocationDropdownOpen((o) => !o);
                }
              }}
              style={{ opacity: loadingCities ? 0.6 : 1, cursor: loadingCities ? "default" : "pointer" }}
            >
              <span>{loadingCities ? "Loading Locations..." : (filters.city ? formatPascal(filters.city) : "All Locations")}</span>
              <FaChevronDown className={`domain-filter-chevron ${locationDropdownOpen ? "open" : ""}`} />
            </div>
            {locationDropdownOpen && !loadingCities && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search locations..."
                  value={locationSearchQuery}
                  onChange={(e) => setLocationSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.city ? "selected" : ""}`}
                    onClick={() => {
                      handleFilterChange("city", "");
                      setLocationDropdownOpen(false);
                      setLocationSearchQuery("");
                    }}
                  >
                    All Locations
                  </div>
                  {(() => {
                    const sortedCities = [...allCities].sort((a, b) =>
                      String(a.city_name || a.City_Name || "").localeCompare(String(b.city_name || b.City_Name || ""))
                    );
                    const filteredCities = sortedCities.filter((c) => {
                      const name = c.city_name || c.City_Name || "";
                      return !locationSearchQuery.trim() || String(name).toLowerCase().includes(locationSearchQuery.trim().toLowerCase());
                    });
                    return filteredCities.length > 0 ? (
                      filteredCities.map((c) => {
                        const cityName = c.city_name || c.City_Name || "";
                        return (
                          <div
                            key={c.city_id}
                            className={`domain-filter-option ${filters.city === cityName ? "selected" : ""}`}
                            onClick={() => {
                              handleFilterChange("city", cityName);
                              setLocationDropdownOpen(false);
                              setLocationSearchQuery("");
                            }}
                          >
                            {formatPascal(cityName)}
                          </div>
                        );
                      })
                    ) : (
                      <div className="domain-filter-no-results">No Locations Found</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <div className="domain-filter-dropdown" ref={serviceDropdownRef}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              onClick={() => setServiceDropdownOpen((o) => !o)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setServiceDropdownOpen((o) => !o);
                }
              }}
            >
              <span>
                {filters.service === "All Services" || !filters.service
                  ? "All Services"
                  : formatPascal(filters.service)}
              </span>
              <FaChevronDown className={`domain-filter-chevron ${serviceDropdownOpen ? "open" : ""}`} />
            </div>
            {serviceDropdownOpen && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search services..."
                  value={serviceSearchQuery}
                  onChange={(e) => setServiceSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.service || filters.service === "All Services" ? "selected" : ""}`}
                    onClick={() => {
                      handleFilterChange("service", "All Services");
                      setServiceDropdownOpen(false);
                      setServiceSearchQuery("");
                    }}
                  >
                    All Services
                  </div>
                  {(() => {
                    const filteredServices = services.filter(
                      (s) =>
                        s === "All Services" ||
                        !serviceSearchQuery.trim() ||
                        String(s).toLowerCase().includes(serviceSearchQuery.trim().toLowerCase())
                    );
                    const filteredWithoutAll = filteredServices.filter((s) => s !== "All Services");
                    return filteredWithoutAll.length > 0 ? (
                      filteredWithoutAll.map((s) => (
                        <div
                          key={s}
                          className={`domain-filter-option ${filters.service === s ? "selected" : ""}`}
                          onClick={() => {
                            handleFilterChange("service", s);
                            setServiceDropdownOpen(false);
                            setServiceSearchQuery("");
                          }}
                        >
                          {formatPascal(s)}
                        </div>
                      ))
                    ) : (
                      <div className="domain-filter-no-results">No Services Found</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <div className="domain-filter-dropdown" ref={domainDropdownRef}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              onClick={() => setDomainDropdownOpen((o) => !o)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setDomainDropdownOpen((o) => !o);
                }
              }}
            >
              <span>{filters.domain ? formatPascal(filters.domain) : "All Businesses"}</span>
              <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
            </div>
            {domainDropdownOpen && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search businesses..."
                  value={domainSearchQuery}
                  onChange={(e) => setDomainSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.domain ? "selected" : ""}`}
                    onClick={() => {
                      handleFilterChange("domain", "");
                      setDomainDropdownOpen(false);
                      setDomainSearchQuery("");
                    }}
                  >
                    All Businesses
                  </div>
                  {(() => {
                    const filteredDomains = domains
                      .map((d) => d.name)
                      .filter((name) => !domainSearchQuery.trim() || String(name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase()))
                      .sort();
                    return filteredDomains.length > 0 ? (
                      filteredDomains.map((dom) => (
                        <div
                          key={dom}
                          className={`domain-filter-option ${filters.domain === dom ? "selected" : ""}`}
                          onClick={() => {
                            handleFilterChange("domain", dom);
                            setDomainDropdownOpen(false);
                            setDomainSearchQuery("");
                          }}
                        >
                          {formatPascal(dom)}
                        </div>
                      ))
                    ) : (
                      <div className="domain-filter-no-results">No Businesses Found</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <select
            className="search-filter-input search-filter-select"
            title="Filter clients by status"
            value={filters.status || ""}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <label className="customers-date-filter-field">
            <span className="customers-date-filter-field__label">Created From</span>
            <input
              type="date"
              className="search-filter-input"
              title="Created from"
              value={filters.createdFrom}
              onChange={(e) => handleFilterChange("createdFrom", e.target.value)}
            />
          </label>
          <label className="customers-date-filter-field">
            <span className="customers-date-filter-field__label">Created To</span>
            <input
              type="date"
              className="search-filter-input"
              title="Created to"
              value={filters.createdTo}
              onChange={(e) => handleFilterChange("createdTo", e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn-clear"
            onClick={handleClearFilters}
          >
            Clear
          </button>
          {!isCompactViewport ? pageActions : null}
        </div>
        </div>
        {isCompactViewport ? pageActions : null}
      </div>

      {/* Search across columns + rows per page — above table */}
      <div className="search-row-2 search-row-2--with-rows co-results-sticky">
        <div className="free-search-bar">
          <input
            type="text"
            className="free-search-input"
            placeholder="Search..."
            value={filters.freeSearch}
            onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
            title="Search across all columns"
          />
        </div>
        <RowsPerPageSelect
          id="customers-rows-per-page"
          value={rowsPerPage}
          options={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onChange={handleRowsPerPageChange}
        />
      </div>

      <div className="table-wrapper co-desktop-table">
        <div className="table-container">
          <table className="data-table leads-table-frozen">
            <thead>
              <tr>
                <th
                  className="col-sticky col-0 digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(0) || "150px",
                    width: getColumnWidth(0)
                  }}
                >
                  <div className="digix-th-sort-row">
                    <SupportSortableHeader columnId="companyName" label="Company Name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 0)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(1) || "120px",
                    width: getColumnWidth(1),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="domain" label="Business" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 1)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(2) || "140px",
                    width: getColumnWidth(2),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="name" label="Full Name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 2)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(3) || "120px",
                    width: getColumnWidth(3),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="customerType" label="Client type" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 3)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(4) || "110px",
                    width: getColumnWidth(4),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="phone" label="Contact" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 4)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(5) || "120px",
                    width: getColumnWidth(5),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="location" label="Location" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 5)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable services-preview-th"
                  style={{
                    minWidth: getColumnMinWidth(6) || "50ch",
                    width: getColumnWidth(6) || "50ch",
                    maxWidth: "50ch",
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="services" label="Services" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 6)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(7) || "110px",
                    width: getColumnWidth(7),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 7)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable notes-preview-th"
                  style={{
                    minWidth: getColumnMinWidth(8) || "160px",
                    width: getColumnWidth(8) || "160px",
                    maxWidth: "180px",
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="notes" label="Notes" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 8)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(9) || "120px",
                    width: getColumnWidth(9) || "120px",
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="createdAt" label="Created On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 9)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(10) || "120px",
                    width: getColumnWidth(10),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="createdBy" label="Created By" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 10)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(11) || "120px",
                    width: getColumnWidth(11),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="updatedBy" label="Updated By" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 11)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(12) || "120px",
                    width: getColumnWidth(12) || "120px",
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="updatedAt" label="Updated On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 12)}
                  ></span>
                </th>
                <th
                  className="digix-th-sortable"
                  style={{
                    minWidth: getColumnMinWidth(13) || "130px",
                    width: getColumnWidth(13),
                    position: "relative"
                  }}
                >
                  <div className="digix-th-sort-row"><SupportSortableHeader columnId="source" label="Source" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 13)}
                  ></span>
                </th>
                <th
                  className="col-sticky col-action"
                  style={{
                    minWidth: getColumnMinWidth(14) || "220px",
                    width: getColumnWidth(14)
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>Action</div>
                  <span
                    className="column-resizer"
                    onMouseDown={(e) => handleResizeStart(e, 14)}
                  ></span>
                </th>
              </tr>
            </thead>
            <tbody>
              <CustomersTable
                rows={currentLeads}
                courses={[]}
                servicesData={servicesData}
                domains={domains}
                getColumnWidth={getColumnWidth}
                getColumnMinWidth={getColumnMinWidth}
                handleView={(id) => onViewLead && onViewLead(id, "client")}
                handleEdit={(id) => onEditLead && onEditLead(id, "client")}
                handleDelete={onDeleteTenant}
              />
            </tbody>
          </table>
        </div>
      </div>

      <ResultsMobileList emptyLabel="No clients found.">
        {currentLeads.map((row) => {
          const ownerName =
            formatFullName(row.name, row.firstName || row.first_name, row.lastName || row.last_name) ||
            (row.name && row.name !== "—" ? row.name : "");
          const phoneDigits = digitsOnly(row.phone);
          const storedCc = String(row.countryCode || row.Country_Code || "").trim();
          let cc = "";
          let local = phoneDigits;
          if (phoneDigits) {
            if (storedCc) {
              cc = normalizeCountryCode(storedCc, "");
              const ccDigits = digitsOnly(cc);
              if (ccDigits && phoneDigits.startsWith(ccDigits) && phoneDigits.length > ccDigits.length) {
                local = phoneDigits.slice(ccDigits.length);
              } else if (phoneDigits.length > 10) {
                local = phoneDigits.slice(-10);
              }
            } else if (phoneDigits.length > 10) {
              cc = `+${phoneDigits.slice(0, phoneDigits.length - 10)}`;
              local = phoneDigits.slice(-10);
            }
          }
          const phoneDisplay =
            row.phone && row.phone !== "—"
              ? joinPhoneDisplay(cc, local) || formatPhone(row.phone) || row.phone
              : null;
          const telHref = phoneDigits ? `tel:${joinPhoneE164(cc, local) || phoneDigits}` : null;
          const waNumber = (joinPhoneE164(cc, local) || phoneDigits || "").replace(/\D/g, "");
          const waHref = waNumber ? `https://wa.me/${waNumber}` : null;
          const status = row.status && row.status !== "—" ? row.status : null;
          const rawCompany = row.companyName || row.company_name || row.Company_Name || null;
          const company = rawCompany && rawCompany !== "—" ? rawCompany : null;
          const rawType = row.customerType || row.customer_type || row.tenantType || null;
          const customerType = rawType && rawType !== "—" ? rawType : null;
          const locationLabel = (() => {
            const raw = row.locationShort && row.locationShort !== "—" ? row.locationShort : null;
            if (!raw) return null;
            const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
            if (parts.length < 2) return formatPascal(raw);
            const countryPart = parts.pop();
            return `${formatPascal(parts.join(", "))}, ${countryPart}`;
          })();
          const companyPhoneDigits = digitsOnly(row.companyPhone);
          const companyE164 = (() => {
            if (!companyPhoneDigits) return "";
            if (companyPhoneDigits.length > 10) return `+${companyPhoneDigits}`;
            const companyCc = cc || normalizeCountryCode(storedCc, "");
            return companyCc ? joinPhoneE164(companyCc, companyPhoneDigits) || companyPhoneDigits : companyPhoneDigits;
          })();
          const companyTelHref = companyE164 ? `tel:${companyE164}` : null;
          const companyPhoneDisplay = companyE164 ? formatPhone(companyE164) || companyE164 : "";
          const companyWaNumber = companyE164.replace(/\D/g, "");
          const companyWaHref = companyWaNumber ? `https://wa.me/${companyWaNumber}` : null;
          const business = row.domain || row.Domain || row.business || null;
          const personalEmail = row.email && row.email !== "—" ? formatEmail(row.email) || row.email : null;
          const companyEmail =
            row.companyEmail && row.companyEmail !== "—"
              ? formatEmail(row.companyEmail) || row.companyEmail
              : null;

          const ownerBadge =
            row.ownerBusinessCount > 1 ? (
              <span
                className="co-tcard__owner-companies"
                title={`Owner of ${row.ownerBusinessCount} companies`}
                aria-label={`Owner of ${row.ownerBusinessCount} companies`}
              >
                <FaBuilding aria-hidden="true" />+{row.ownerBusinessCount - 1}
              </span>
            ) : null;

          const statusTone = (() => {
            const v = String(status || "").trim().toLowerCase();
            if (/(won|qualified|converted|completed|active)/.test(v)) return "success";
            if (/(lost|rejected|cancelled|canceled|closed|inactive)/.test(v)) return "danger";
            if (/(contacted|follow|pending|progress|warm)/.test(v)) return "warning";
            if (/(new|open|cold)/.test(v)) return "info";
            return "neutral";
          })();

          return (
            <ResultsMobileCard
              key={row.id}
              title={company || "—"}
              subtitle={
                ownerBadge ? (
                  <span className="co-tcard__owner-row">
                    <span className="co-tcard__owner-name">{ownerName || "—"}</span>
                    {ownerBadge}
                  </span>
                ) : (
                  ownerName || null
                )
              }
              meta={[
                status ? { label: status, tone: statusTone } : null,
                business ? { label: business, tone: "info" } : null,
              ].filter(Boolean)}
              onOpen={() => onViewLead && onViewLead(row.id, "client")}
              actions={
                <div className="co-mcard__action-grid">
                  {companyTelHref ? (
                    <a className="co-maction" href={companyTelHref} title={companyPhoneDisplay} aria-label={`Call company ${companyPhoneDisplay}`}>
                      <FaPhone size={14} style={{ transform: "scaleX(-1)" }} />
                      <span>Call</span>
                    </a>
                  ) : (
                    <span
                      className="co-maction co-maction--disabled"
                      aria-disabled="true"
                    >
                      <FaPhone size={14} style={{ transform: "scaleX(-1)" }} />
                      <span>Call</span>
                    </span>
                  )}
                  {companyWaHref ? (
                    <a
                      className="co-maction co-maction--whatsapp"
                      href={companyWaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={companyPhoneDisplay}
                      aria-label={`WhatsApp company ${companyPhoneDisplay}`}
                    >
                      <FaWhatsapp size={15} />
                      <span>Chat</span>
                    </a>
                  ) : (
                    <span
                      className="co-maction co-maction--disabled"
                      aria-disabled="true"
                    >
                      <FaWhatsapp size={15} />
                      <span>Chat</span>
                    </span>
                  )}
                  {companyEmail ? (
                    <a className="co-maction" href={`mailto:${companyEmail}`} title={companyEmail} aria-label={`Email company ${companyEmail}`}>
                      <FaEnvelope size={14} />
                      <span>Email</span>
                    </a>
                  ) : (
                    <span
                      className="co-maction co-maction--disabled"
                      aria-disabled="true"
                    >
                      <FaEnvelope size={14} />
                      <span>Email</span>
                    </span>
                  )}
                  {typeof onEditLead === "function" ? (
                    <button
                      type="button"
                      className="co-maction"
                      title="Edit client"
                      aria-label="Edit client"
                      onClick={() => onEditLead(row.id, "client")}
                    >
                      <FaEdit size={14} />
                      <span>Edit</span>
                    </button>
                  ) : null}
                  {typeof onDeleteTenant === "function" ? (
                    <button
                      type="button"
                      className="co-maction co-maction--danger"
                      title="Delete client"
                      aria-label="Delete client"
                      onClick={() => onDeleteTenant(row.id)}
                    >
                      <FaTrashAlt size={14} />
                      <span>Delete</span>
                    </button>
                  ) : null}
                </div>
              }
            >
              {phoneDisplay || personalEmail || waHref ? (
                <ul className="co-tcard__contact">
                  {phoneDisplay ? (
                    <li>
                      <FaPhone aria-hidden="true" style={{ transform: "scaleX(-1)" }} />
                      {telHref ? (
                        <a href={telHref} title="Call personal mobile">{phoneDisplay}</a>
                      ) : (
                        <span>{phoneDisplay}</span>
                      )}
                    </li>
                  ) : null}
                  {personalEmail ? (
                    <li>
                      <FaEnvelope aria-hidden="true" />
                      <a href={`mailto:${personalEmail}`} title="Email personal">{personalEmail}</a>
                    </li>
                  ) : null}
                  {waHref ? (
                    <li className="co-tcard__contact-wa">
                      <FaWhatsapp aria-hidden="true" />
                      <a href={waHref} target="_blank" rel="noopener noreferrer" title="Chat on WhatsApp">
                        {phoneDisplay || "Chat on WhatsApp"}
                      </a>
                    </li>
                  ) : null}
                </ul>
              ) : null}
              {customerType || locationLabel ? (
                <dl className="co-tcard__info co-tcard__info--type-row">
                  {customerType ? (
                    <div className="co-tcard__type">
                      <dt>Type</dt>
                      <dd>
                        <span
                          className="co-tcard__type-icon"
                          title={customerType}
                          aria-label={customerType}
                          role="img"
                        >
                          {/^org/i.test(customerType) ? <FaBuilding /> : <FaUser />}
                        </span>
                      </dd>
                    </div>
                  ) : null}
                  {locationLabel ? (
                    <div>
                      <dt>Location</dt>
                      <dd>{locationLabel}</dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}
            </ResultsMobileCard>
          );
        })}
      </ResultsMobileList>

      {sortedCustomers.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalItems={sortedCustomers.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel="clients"
          rowsPerPageSelectId="customers-rows-per-page"
          showRowsPerPage={false}
        />
      )}
    </div>
  );
}

export default CustomersLeadsPage;

